import {
  BUSINESS_NAME,
  PUBLIC_PHONE,
  BUSINESS_SUMMARY,
  LEGAL_ENTITY,
  SERVICE_TERRITORY
} from './businessContact.js';
import { SITE_ORIGIN, DEFAULT_OG_IMAGE } from './site.js';

export const ORG_ID = `${SITE_ORIGIN}/#organization`;
export const WEBSITE_ID = `${SITE_ORIGIN}/#website`;

const AREA = SERVICE_TERRITORY.primaryCounties.map(name => ({
  '@type': 'AdministrativeArea',
  name
}));

export function organizationNode() {
  return {
    '@type': 'Organization',
    '@id': ORG_ID,
    name: BUSINESS_NAME,
    legalName: LEGAL_ENTITY.legalName,
    url: SITE_ORIGIN,
    description: BUSINESS_SUMMARY,
    image: DEFAULT_OG_IMAGE,
    telephone: PUBLIC_PHONE.e164,
    identifier: {
      '@type': 'PropertyValue',
      propertyID: 'Florida Division of Corporations document number',
      value: LEGAL_ENTITY.documentNumber
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: LEGAL_ENTITY.locality,
      addressRegion: LEGAL_ENTITY.region,
      addressCountry: 'US'
    },
    areaServed: AREA,
    contactPoint: [PUBLIC_PHONE].map((phone, index) => ({
      '@type': 'ContactPoint',
      telephone: phone.e164,
      contactType: index === 0 ? 'customer service' : 'sales',
      areaServed: 'US-FL',
      availableLanguage: 'English'
    }))
  };
}

export function breadcrumbNode(items) {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${SITE_ORIGIN}${item.path}`
    }))
  };
}

export function pageGraph({ path, title, description, type = 'WebPage', crumbs, extra = [] }) {
  const pageId = `${SITE_ORIGIN}${path === '/' ? '' : path}#webpage`;
  const graph = [
    organizationNode(),
    {
      '@type': 'WebSite',
      '@id': WEBSITE_ID,
      url: SITE_ORIGIN,
      name: BUSINESS_NAME,
      publisher: { '@id': ORG_ID }
    },
    {
      '@type': type,
      '@id': pageId,
      url: `${SITE_ORIGIN}${path === '/' ? '/' : path}`,
      name: title,
      description,
      isPartOf: { '@id': WEBSITE_ID },
      about: { '@id': ORG_ID },
      publisher: { '@id': ORG_ID }
    },
    ...extra
  ];
  if (crumbs?.length) graph.push(breadcrumbNode(crumbs));
  return { '@context': 'https://schema.org', '@graph': graph };
}

export function serviceGraph(service, crumbs) {
  const serviceNode = {
    '@type': 'Service',
    '@id': `${SITE_ORIGIN}${service.path}#service`,
    name: service.h1,
    description: service.description,
    serviceType: service.navLabel,
    url: `${SITE_ORIGIN}${service.path}`,
    provider: { '@id': ORG_ID },
    areaServed: AREA
  };
  return pageGraph({
    path: service.path,
    title: service.title,
    description: service.description,
    crumbs,
    extra: [serviceNode]
  });
}

export function articleGraph(page, crumbs) {
  return pageGraph({
    path: page.path,
    title: page.title,
    description: page.description,
    type: 'Article',
    crumbs,
    extra: [
      {
        '@type': 'Article',
        '@id': `${SITE_ORIGIN}${page.path}#article`,
        headline: page.h1,
        description: page.description,
        author: { '@id': ORG_ID },
        publisher: { '@id': ORG_ID },
        mainEntityOfPage: `${SITE_ORIGIN}${page.path}`,
        image: page.ogImage || DEFAULT_OG_IMAGE
      }
    ]
  });
}

export function projectGraph(project, crumbs) {
  const images = (project.images || []).map(image => ({
    '@type': 'ImageObject',
    contentUrl: `${SITE_ORIGIN}${image.src}`,
    description: image.alt,
    width: image.width,
    height: image.height
  }));
  const videos = (project.videos || []).map(video => ({
    '@type': 'VideoObject',
    name: video.name,
    description: video.description,
    contentUrl: `${SITE_ORIGIN}${video.src}`,
    thumbnailUrl: video.poster ? `${SITE_ORIGIN}${video.poster}` : DEFAULT_OG_IMAGE
  }));
  return pageGraph({
    path: project.path,
    title: project.title,
    description: project.description,
    crumbs,
    extra: [...images, ...videos]
  });
}
